import * as React from "react";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fade,
  Grid,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider as MuiLocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Wrapper from "../../../components/Wrapper";
import TenantInfoBanner from "../../../components/TenantInfoBannerOwner";
import { ElectricBill } from "../../../redux/reducer/ElectricReading";
import { WaterReadings } from "../../../redux/reducer/WaterReading";
import { TrashReadings } from "../../../redux/reducer/TrashReading";
import { MaintananceReadings } from "../../../redux/reducer/MaintananceReading";
import { SsgcBill } from "../../../redux/reducer/SsgcReading";
import { BASE_URL } from "../../../config/config";

import BillCards from "./BillCards";
import ConsumerNumberField from "./ConsumerNumberField";
import FileUploadField from "./FileUploadField";
import ViewOnlyModal from "./ViewOnlyModal";
import { UploadLoader, AutoFillLoader } from "./FullScreenLoaders";
import {
  getUploadBillSchema,
  BILL_IMAGE_REQUIRED_TYPES,
} from "./validationSchema";
import { BILL_CARDS, AUTO_FILL_TYPES } from "./constants";
import { BILL_TYPES, BILL_TYPE_META, UPLOAD_TYPE_VALUE, BACKEND_KEY } from "../../../constants/billTypes";
import { getDueBillTypesForThisCycle } from "../../../utils/billVisibility";
import { RentReadings } from "../../../redux/reducer/RentReading";
import { SecurityReadings } from "../../../redux/reducer/SecurityReading";

import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MessageIcon from "@mui/icons-material/Message";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockIcon from "@mui/icons-material/Lock";
import axios from "axios";

// The API speaks "DD-MM-YYYY". dayjs only honours a format string when the
// customParseFormat plugin is registered, and it isn't in this app — so parse
// the parts explicitly instead of silently getting an Invalid Date.
const parseDdMmYyyy = (value) => {
  if (!value) return null;
  const [day, month, year] = String(value).split("-").map(Number);
  if (!day || !month || !year) return null;
  return dayjs(new Date(year, month - 1, day));
};

// The bill-cycle response is keyed by the backend's spellings; the cards are
// keyed by the canonical ones. Inverting BACKEND_KEY keeps the mapping in one
// place instead of a second hand-written table that can fall out of step.
const CANONICAL_FOR_BACKEND_KEY = Object.fromEntries(
  Object.entries(BACKEND_KEY).map(([canonical, backend]) => [backend, canonical])
);

export default function UploadBillModern() {
  const open = useSelector((state) => state.sidebar.open);
  const { tenantId, flatId } = useParams();

  const kElectricLoading = useSelector(
    (state) => state?.electricReading?.loading
  );
  const ssgcLoading = useSelector((state) => state?.ssgcReading?.loading);
  const waterLoading = useSelector((state) => state?.waterReading?.loading);
  const maintenanceLoading = useSelector(
    (state) => state?.maintananceReading?.loading
  );
  const trashLoading = useSelector((state) => state?.trashReading?.loading);
  const rentLoading = useSelector((state) => state?.rentReading?.loading);
  const securityLoading = useSelector(
    (state) => state?.securityReading?.loading
  );

  const electricState = useSelector((state) => state?.electricReading);
  const ssgcState = useSelector((state) => state?.ssgcReading);
  const waterState = useSelector((state) => state?.waterReading);
  const maintenanceState = useSelector((state) => state?.maintananceReading);
  const trashState = useSelector((state) => state?.trashReading);
  const rentState = useSelector((state) => state?.rentReading);
  const securityState = useSelector((state) => state?.securityReading);

  // ── Tenant info for banner ─────────────────────────────────────────────────
  const validateUSER = useSelector(
    (state) => state?.validateUser?.UserValidate?.data
  );
  const flats = useSelector(
    (state) => state?.validateUser?.UserValidate?.data?.user?.flats
  );

  const getId = window.location.pathname.replace("/upload-Bill/", "");
  const [urlTenantId, urlFlatId] = getId.split("/");

  const activeTenantId = tenantId || urlTenantId;
  const activeFlatId = flatId || urlFlatId;

  // Find tenant from requests or property detail (same logic as LandlordFunctionality)
  const requests = validateUSER?.user?.requests || [];
  const tenantFromRequests = requests.find(
    (req) =>
      String(req.tenantId) === String(activeTenantId) &&
      String(req.flatId) === String(activeFlatId)
  );
  const tenantFromProperty = flats
    ?.flatMap((item) => item.propertyDetail || [])
    ?.find(
      (property) =>
        String(property._id) === String(activeFlatId) &&
        String(property.tenantId) === String(activeTenantId)
    );

  const displayTenant = tenantFromRequests || tenantFromProperty;
  // ─────────────────────────────────────────────────────────────────────────

  const bill_uploaded_by = useSelector(
    (state) =>
      state?.validateUser?.UserValidate?.data?.tenantProfile
        ?.flatInterestRequests
  );
  const rmUploadBill = window.location.pathname.replace("/upload-Bill/", "");

  // This is the LANDLORD's upload screen, so the flat has to be read off the
  // landlord's own `user.flats`. `tenantProfile.flatInterestRequests` only
  // exists on a tenant's validateUser payload — here it was always undefined,
  // which meant every fixed-charge amount (rent, security, …) stayed blank in
  // an already-disabled field, and getDueBillTypesForThisCycle fell back to
  // its no-property default. Kept as a fallback for the tenant-uploader case.
  const flatDetail = React.useMemo(() => {
    const fromOwnedFlats = flats
      ?.flatMap((item) => item.propertyDetail || [])
      ?.find((property) => String(property._id) === String(activeFlatId));

    if (fromOwnedFlats) return fromOwnedFlats;

    return bill_uploaded_by?.find(
      (item) => String(item?.flatDetail?._id) === String(activeFlatId)
    )?.flatDetail;
  }, [flats, bill_uploaded_by, activeFlatId]);

  const isTenantUploader = flatDetail?.bill_uploaded_by === "tenant";

  // ── Billing cycle status ──────────────────────────────────────────────────
  // Authoritative answer to "what has already been uploaded for this cycle,
  // and can it still be changed". Without this the screen could only guess
  // from session-local state, so the landlord discovered a duplicate by
  // submitting and getting "Bill Already exists" back — with no way forward.
  const [cycleStatus, setCycleStatus] = React.useState(null);

  const fetchCycleStatus = React.useCallback(() => {
    if (!activeTenantId || !activeFlatId) return;
    axios
      .get(`${BASE_URL}/bill-cycle-status/${activeTenantId}/${activeFlatId}`)
      .then((res) => {
        if (res.data?.success) setCycleStatus(res.data);
      })
      .catch(() => setCycleStatus(null));
  }, [activeTenantId, activeFlatId]);

  React.useEffect(() => { fetchCycleStatus(); }, [fetchCycleStatus]);

  const cycle = cycleStatus?.cycle;

  // Canonical BILL_TYPES key -> the key the status payload uses.
  const statusOf = React.useCallback(
    (type) => cycleStatus?.bills?.[BACKEND_KEY[type]] || null,
    [cycleStatus]
  );

  const statusFor = React.useCallback(
    (type) => {
      const bill = statusOf(type);

      if (bill?.skipped) {
        return { state: "skipped", disabled: true, hint: "Already settled" };
      }
      if (bill?.status === "approved") {
        return {
          state: "approved",
          disabled: true,
          amount: bill.amount,
          hint: "Tenant approved",
        };
      }
      if (bill?.status === "rejected") {
        return { state: "rejected", amount: bill.amount, hint: "Re-upload needed" };
      }
      if (bill?.exists) {
        return { state: "uploaded", amount: bill.amount, hint: "Tap to edit" };
      }
      if (cycle && !cycle.isRaised) {
        return {
          state: "locked_cycle",
          disabled: true,
          amount: bill?.suggestedAmount,
          hint: `Opens ${cycle.raisedOn}`,
        };
      }
      return { state: "ready", amount: bill?.suggestedAmount };
    },
    [statusOf, cycle]
  );

  // ── UI state ──────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = React.useState(null);
  const [viewMode, setViewMode] = React.useState(false);
  // Non-null when the open modal is correcting an existing bill rather than
  // creating one — drives update-vs-create on submit.
  const [editingBillId, setEditingBillId] = React.useState(null);
  const [forceStopLoading, setForceStopLoading] = React.useState(false);
  // Session-local "this month's bill" running total — amounts the landlord
  // has entered so far in this visit, purely for the summary strip below.
  // Not authoritative (each type still submits to its own endpoint).
  const [submittedAmounts, setSubmittedAmounts] = React.useState({});

  // Which charges are actually payable this cycle is the server's call: it
  // holds the monthly/annual schedule the owner set at registration, so an
  // annual trash charge set to June only offers a card in June. The local
  // helper is only a fallback for the moment before that response lands.
  const dueBillTypes = React.useMemo(() => {
    if (!cycleStatus?.bills) return getDueBillTypesForThisCycle(flatDetail);

    return Object.entries(cycleStatus.bills)
      .filter(([, bill]) => !bill.skipped || bill.exists)
      .map(([key]) => CANONICAL_FOR_BACKEND_KEY[key])
      .filter(Boolean);
  }, [cycleStatus, flatDetail]);

  const visibleCards = BILL_CARDS.filter((c) => dueBillTypes.includes(c.type));
  const runningTotal = Object.values(submittedAmounts).reduce(
    (sum, amt) => sum + (Number(amt) || 0),
    0
  );

  // ── File state ────────────────────────────────────────────────────────────
  const [imagePreview, setImagePreview] = React.useState("");
  const [previewFile, setPreviewFile] = React.useState(null);
  const [pdfBlob, setPdfBlob] = React.useState(null);

  // ── Consumer number ───────────────────────────────────────────────────────
  const [consumerNumber, setConsumerNumber] = React.useState("");
  const [consumerNumberError, setConsumerNumberError] = React.useState("");

  // ── Auto-fill / fetch state ───────────────────────────────────────────────
  const [autoFillLoading, setAutoFillLoading] = React.useState(false);
  const [billFetched, setBillFetched] = React.useState(false);
  const [fetchedBillAmount, setFetchedBillAmount] = React.useState(null);
  const [fetchedDueDate, setFetchedDueDate] = React.useState(null);

  const fieldsLocked = billFetched;

  // Rent/Maintenance/Water/Security/Trash amounts are already fixed at
  // property registration — the owner shouldn't re-enter them here. Only
  // Electricity/Gas genuinely vary bill-to-bill and need auto-fetch/manual
  // entry. For fixed types, the amount is auto-filled + locked and the
  // owner only needs to confirm a due date.
  const FIXED_CHARGE_TYPES = [
    BILL_TYPES.RENT,
    BILL_TYPES.MAINTENANCE,
    BILL_TYPES.WATER,
    BILL_TYPES.SECURITY,
    BILL_TYPES.TRASH,
  ];
  const isFixedChargeType = FIXED_CHARGE_TYPES.includes(modalOpen);
  const amountLocked = fieldsLocked || isFixedChargeType;

  // Rent and security live as flat scalar fields on the property; the
  // frequency-capable charges (maintenance/water/trash) are nested objects.
  const getConfiguredAmount = (type) => {
    if (type === BILL_TYPES.RENT) return flatDetail?.flatRent;
    if (type === BILL_TYPES.SECURITY) {
      return flatDetail?.security?.amount ?? flatDetail?.flatSecurityCharges;
    }
    return flatDetail?.[type]?.amount;
  };

  const dispatch = useDispatch();
  const prevLoadingRef = React.useRef({
    electric: false,
    gas: false,
    water: false,
    maintenance: false,
    trash: false,
    rent: false,
    security: false,
  });
  const loadingTimeoutRef = React.useRef(null);

  const isAnyLoading =
    !forceStopLoading &&
    (kElectricLoading ||
      ssgcLoading ||
      waterLoading ||
      maintenanceLoading ||
      trashLoading ||
      rentLoading ||
      securityLoading);
  const cardData = BILL_CARDS.find((c) => c.type === modalOpen);
  const showAutoFill = AUTO_FILL_TYPES.includes(modalOpen);
  const requiresImage = BILL_IMAGE_REQUIRED_TYPES.includes(modalOpen);
  const consumerNumLength = modalOpen === BILL_TYPES.GAS ? 10 : 13;

  // ── API response watcher ──────────────────────────────────────────────────
  React.useEffect(() => {
    const watch = (state, wasLoading, isLoading, name) => {
      if (wasLoading && !isLoading) {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setForceStopLoading(false);
        if (state?.error || state?.isError || state?.failed) {
          const msg =
            state?.error?.message ||
            state?.error?.data?.message ||
            state?.errorMessage ||
            `Failed to upload ${name} bill`;
          toast.error(msg, {
            position: "top-right",
            autoClose: 5000,
            theme: "colored",
          });
        } else if (
          state?.success ||
          state?.data ||
          state?.electricBill ||
          state?.ssgcBill ||
          state?.waterReading ||
          state?.maintananceReading ||
          state?.trashReading
        ) {
          setTimeout(() => handleCloseModal(), 800);
        }
      }
      if (!wasLoading && isLoading) {
        loadingTimeoutRef.current = setTimeout(() => {
          toast.error(`${name} upload is taking too long. Please try again.`, {
            position: "top-right",
            autoClose: 5000,
            theme: "colored",
          });
          setForceStopLoading(true);
        }, 30000);
      }
    };
    watch(
      electricState,
      prevLoadingRef.current.electric,
      kElectricLoading,
      "Electric"
    );
    watch(ssgcState, prevLoadingRef.current.gas, ssgcLoading, "Gas");
    watch(waterState, prevLoadingRef.current.water, waterLoading, "Water");
    watch(
      maintenanceState,
      prevLoadingRef.current.maintenance,
      maintenanceLoading,
      "Maintenance"
    );
    watch(trashState, prevLoadingRef.current.trash, trashLoading, "Trash");
    watch(rentState, prevLoadingRef.current.rent, rentLoading, "Rent");
    watch(
      securityState,
      prevLoadingRef.current.security,
      securityLoading,
      "Security"
    );
    prevLoadingRef.current = {
      electric: kElectricLoading,
      gas: ssgcLoading,
      water: waterLoading,
      maintenance: maintenanceLoading,
      trash: trashLoading,
      rent: rentLoading,
      security: securityLoading,
    };
    return () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, [
    kElectricLoading,
    ssgcLoading,
    waterLoading,
    maintenanceLoading,
    trashLoading,
    rentLoading,
    securityLoading,
    electricState,
    ssgcState,
    waterState,
    maintenanceState,
    trashState,
    rentState,
    securityState,
  ]);

  // ── Reset helpers ─────────────────────────────────────────────────────────
  const resetFetchState = () => {
    setBillFetched(false);
    setFetchedBillAmount(null);
    setFetchedDueDate(null);
  };

  // ── Modal open/close ──────────────────────────────────────────────────────
  const handleOpenModal = (type) => {
    setViewMode(!!isTenantUploader);
    setModalOpen(type);
    setImagePreview("");
    setPreviewFile(null);
    setConsumerNumber("");
    setConsumerNumberError("");
    resetFetchState();
    formik.resetForm();

    const bill = statusOf(type);

    // Editing an already-uploaded bill: load what's there so the landlord
    // corrects a figure rather than retyping the whole thing.
    setEditingBillId(bill?.exists && !bill?.locked ? bill.billId : null);

    // Amount: existing bill first, then the (pro-rated) figure the server
    // derived from property registration. Metered types stay blank — they
    // have to come off the real utility bill.
    const prefillAmount = bill?.exists
      ? bill.amount
      : bill?.suggestedAmount ?? getConfiguredAmount(type);

    if (prefillAmount) {
      formik.setFieldValue("total_amount", prefillAmount, false);
    }

    // Due date is derived from the property's rentDueDate — never asked for.
    const prefillDue = bill?.dueDate || bill?.suggestedDueDate || cycle?.dueDate;
    const parsedDue = parseDdMmYyyy(prefillDue);
    if (parsedDue) {
      formik.setFieldValue("due_date", parsedDue, false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(null);
    setViewMode(false);
    setEditingBillId(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    setPreviewFile(null);
    setPdfBlob(null);
    setConsumerNumber("");
    setConsumerNumberError("");
    resetFetchState();
    formik.resetForm();
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    setForceStopLoading(false);
  };

  // ── File handlers ─────────────────────────────────────────────────────────
  const handleFileSelect = (file, force = false) => {
    if (!file || (fieldsLocked && !force)) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    const url = URL.createObjectURL(file);
    setImagePreview(url);
    setPreviewFile(file);
    formik.setFieldValue("bill_image", file);
  };

  const handleFileRemove = () => {
    if (fieldsLocked) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    setPreviewFile(null);
    setPdfBlob(null);
    formik.setFieldValue("bill_image", null);
    formik.setFieldTouched("bill_image", true, true);
  };

  const handleDownloadPdf = () => {
    const source = pdfBlob || null;
    if (source) {
      const freshUrl = URL.createObjectURL(source);
      const a = document.createElement("a");
      a.href = freshUrl;
      a.download = previewFile?.name || "bill.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(freshUrl), 60_000);
    } else if (imagePreview) {
      const a = document.createElement("a");
      a.href = imagePreview;
      a.download = previewFile?.name || "bill.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleOpenPdfNewTab = () => {
    const source = pdfBlob || null;
    if (source) {
      const freshUrl = URL.createObjectURL(source);
      const win = window.open("", "_blank");
      if (win) {
        win.location.href = freshUrl;
        setTimeout(() => URL.revokeObjectURL(freshUrl), 60_000);
      } else {
        const a = document.createElement("a");
        a.href = freshUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(freshUrl), 60_000);
      }
    } else if (imagePreview) {
      window.open(imagePreview, "_blank", "noopener,noreferrer");
    }
  };

  // ── Consumer number change ────────────────────────────────────────────────
  const handleConsumerNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    const maxLength = consumerNumLength;
    if (value.length <= maxLength) {
      setConsumerNumber(value);
      setConsumerNumberError(
        value.length > 0 && value.length < maxLength
          ? `Consumer number must be exactly ${maxLength} digits`
          : ""
      );
      if (billFetched) {
        resetFetchState();
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview("");
        setPreviewFile(null);
        formik.setFieldValue("bill_image", null);
        formik.setFieldValue("total_amount", "");
        formik.setFieldValue("due_date", null);
      }
    }
  };

  const handleConsumerReset = () => {
    resetFetchState();
    setConsumerNumber("");
    setConsumerNumberError("");
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    setPreviewFile(null);
    setPdfBlob(null);
    formik.setFieldValue("bill_image", null);
    formik.setFieldValue("total_amount", "");
    formik.setFieldValue("due_date", null);
  };

  // ── Auto-fill ─────────────────────────────────────────────────────────────
  const handleAutoFill = async () => {
    const isEnabled =
      consumerNumber.length === consumerNumLength &&
      !autoFillLoading &&
      !billFetched;
    if (!isEnabled) return;

    setAutoFillLoading(true);
    try {
      const isGas = modalOpen === "gas" || modalOpen === "ssgc";
      const endpoint = isGas
        ? `${BASE_URL}/fetch-auto-ssgc-bill`
        : `${BASE_URL}/fetch-auto-ke-bill`;
      const bodyKey = isGas ? "consumerNumber" : "accountNumber";

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [bodyKey]: consumerNumber }),
      });
      const data = await res.json();

      if (data.success && data.bill) {
        console.log(data, "data");

        const rawAmount = String(data.bill.payableAfter).replace(/[^0-9]/g, "");
        const dueDate = dayjs(data.bill.dueDate);

        let blob;
        let pdfFile;

        // ✅ CASE 1: Already Base64
        if (data.bill.pdfBase64) {
          const bytes = atob(data.bill.pdfBase64);
          const arr = new Uint8Array(
            Array.from(bytes).map((c) => c.charCodeAt(0))
          );
          blob = new Blob([arr], { type: "application/pdf" });
        }

        // ✅ CASE 2: URL → call your API
        else if (data.bill.pdfUrl) {
          const res = await fetch(`${BASE_URL}/convert-pdf`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              pdfUrl: data.bill.pdfUrl,
            }),
          });

          const result = await res.json();

          if (result.success && result.pdfBase64) {
            const bytes = atob(result.pdfBase64);
            const arr = new Uint8Array(
              Array.from(bytes).map((c) => c.charCodeAt(0))
            );
            blob = new Blob([arr], { type: "application/pdf" });
          }
        }

        if (blob) {
          pdfFile = new File([blob], "auto-bill.pdf", {
            type: "application/pdf",
          });

          setPdfBlob(blob);
          handleFileSelect(pdfFile, true);
        }

        formik.setFieldValue("total_amount", rawAmount, false);
        formik.setFieldValue("due_date", dueDate, false);

        setTimeout(() => {
          formik.validateForm();
        }, 100);

        setFetchedBillAmount(rawAmount);
        setFetchedDueDate(dueDate);

        if (blob) {
          setBillFetched(true);
          toast.success(
            data.bill.pdfGenerated
              ? "Bill fetched! (Showing a system-generated copy — the original SSGC scanned bill wasn't available.)"
              : "Bill fetched successfully!",
            {
              position: "top-right",
              autoClose: data.bill.pdfGenerated ? 5000 : 3000,
              theme: "colored",
            }
          );
        } else {
          // Bill amount/due date were fetched, but the PDF itself could not
          // be retrieved from the source — don't claim full success, and
          // don't mark as fetched so the user can retry or upload manually.
          toast.error(
            "Bill amount fetched, but PDF could not be retrieved. Please upload the bill PDF manually.",
            {
              position: "top-right",
              autoClose: 5000,
              theme: "colored",
            }
          );
        }
      } else {
        toast.error("Could not fetch bill. Please fill manually.", {
          position: "top-right",
          autoClose: 4000,
          theme: "colored",
        });
      }
    } catch {
      toast.error("Auto-fill failed. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        theme: "colored",
      });
    } finally {
      setAutoFillLoading(false);
    }
  };

  // ── Formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      total_amount: "",
      message: "",
      due_date: null,
      bill_image: null,
    },
    validationSchema: () => getUploadBillSchema(modalOpen),
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: false,
    onSubmit: async (values, action) => {
      if (isTenantUploader) {
        toast.warning(
          "Upload not allowed — tenant has bill upload permission.",
          { position: "top-right", autoClose: 4000, theme: "colored" }
        );
        return;
      }
      const formData = new FormData();
      const cleanAmount = String(values.total_amount)
        .replace(/[^0-9.]/g, "")
        .trim();
      formData.append("total_amount", cleanAmount);
      formData.append("message", values.message || "");
      formData.append(
        "due_date",
        values.due_date ? dayjs(values.due_date).format("DD-MM-YYYY") : ""
      );
      formData.append("upload_date", dayjs().format("DD-MM-YYYY"));
      // Preserve the exact "type" spelling the backend has always received for
      // the 5 pre-existing categories; canonical spelling is only internal.
      formData.append("type", UPLOAD_TYPE_VALUE[modalOpen] || modalOpen);
      formData.append("tenantId", activeTenantId);
      formData.append("flatId", activeFlatId);
      if (AUTO_FILL_TYPES.includes(modalOpen) && consumerNumber) {
        formData.append("consumer_number", consumerNumber);
      }
      if (values.bill_image) formData.append("bill_image", values.bill_image);

      const API_MAP = {
        [BILL_TYPES.ELECTRICITY]: ElectricBill,
        [BILL_TYPES.GAS]: SsgcBill,
        [BILL_TYPES.WATER]: WaterReadings,
        [BILL_TYPES.MAINTENANCE]: MaintananceReadings,
        [BILL_TYPES.TRASH]: TrashReadings,
        [BILL_TYPES.RENT]: RentReadings,
        [BILL_TYPES.SECURITY]: SecurityReadings,
      };
      const apiCall = API_MAP[modalOpen];
      if (!apiCall) return;
      setSubmittedAmounts((prev) => ({ ...prev, [modalOpen]: cleanAmount }));

      // The update controllers read the bill id off the body; the thunks put
      // it in the URL. Send both so either path resolves it.
      if (editingBillId) formData.append("id", editingBillId);

      dispatch(apiCall({ id: editingBillId || undefined, formData })).then(() =>
        fetchCycleStatus()
      );
    },
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Wrapper open={open} />
      <UploadLoader open={isAnyLoading} cardData={cardData} />
      <AutoFillLoader open={autoFillLoading} cardData={cardData} />

      <Box
        className={`${open ? "sidebar-open" : "sidebar-closed"}`}
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
          pt: 4,
          pb: 8,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Breadcrumbs */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 4,
              borderRadius: 3,
              backgroundColor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Breadcrumbs
              sx={{
                "& a": {
                  color: "#64748b",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  "&:hover": { color: "#6366F1" },
                },
              }}
            >
              <Link to="/landlord-dashboard">Home</Link>
              <Link to="/my-tenants">My Tenants</Link>
              <Link to={`/landlord-functionality/${rmUploadBill}`}>
                Landlord Functionality
              </Link>
              <Typography
                sx={{ color: "#1e293b", fontWeight: 600, fontSize: "0.875rem" }}
              >
                Bill Management
              </Typography>
            </Breadcrumbs>
          </Paper>

          {/* ✅ Tenant Info Banner — page level */}
          {displayTenant && <TenantInfoBanner tenant={displayTenant} />}

          {/* Header */}
          <Box sx={{ mb: 5, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#fff",
                mb: 2,
                textShadow: "0 2px 20px rgba(0,0,0,0.2)",
                letterSpacing: "-0.5px",
              }}
            >
              Utility Bill Management
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontWeight: 400,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              {isTenantUploader
                ? "View bills uploaded by tenant"
                : "Upload and manage utility bills for your property"}
            </Typography>
            {isTenantUploader && (
              <Alert
                severity="info"
                icon={<InfoOutlinedIcon />}
                sx={{
                  mt: 3,
                  maxWidth: 600,
                  mx: "auto",
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  "& .MuiAlert-icon": { color: "#0EA5E9" },
                }}
              >
                <AlertTitle sx={{ fontWeight: 700, color: "#0f172a" }}>
                  View-Only Mode
                </AlertTitle>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Tenant has permission to upload bills. You can view bills but
                  cannot upload new ones.
                </Typography>
              </Alert>
            )}
          </Box>

          {runningTotal > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                backgroundColor: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                  This cycle so far:
                </Typography>
                {Object.entries(submittedAmounts).map(([type, amt]) => (
                  <Box
                    key={type}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      backgroundColor: BILL_TYPE_META[type]?.bgColor || "#f1f5f9",
                    }}
                  >
                    <Typography sx={{ fontSize: "1rem" }}>
                      {BILL_TYPE_META[type]?.icon}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>
                      Rs {Number(amt || 0).toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: "#6366F1" }}>
                Total: Rs {runningTotal.toLocaleString()}
              </Typography>
            </Paper>
          )}

          {/* Cycle banner — answers "why can't I upload yet / why is rent
              missing" before the landlord clicks anything. */}
          {cycle && !isTenantUploader && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 3,
                backgroundColor: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(10px)",
                borderLeft: `5px solid ${cycle.isRaised ? "#10B981" : "#F59E0B"}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0f172a" }}>
                  {cycle.cycleLabel} cycle
                </Typography>
                <Chip
                  size="small"
                  label={cycle.isRaised ? "Billed" : `Opens ${cycle.raisedOn}`}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: cycle.isRaised ? "#D1FAE5" : "#FEF3C7",
                    color: cycle.isRaised ? "#047857" : "#B45309",
                  }}
                />
                <Chip size="small" variant="outlined" label={`Due ${cycle.dueDate}`} />
                {cycle.lateSummary && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Late charges from ${cycle.deadline}: ${cycle.lateSummary}`}
                  />
                )}
                {cycle.isPartial && (
                  <Chip
                    size="small"
                    label={`Partial month — ${cycle.occupiedDays} of ${cycle.daysInMonth} days`}
                    sx={{ fontWeight: 700, backgroundColor: "#EDE9FE", color: "#6D28D9" }}
                  />
                )}
              </Box>

              <Typography sx={{ mt: 1, color: "#64748b", fontSize: "0.85rem" }}>
                {cycle.isRaised
                  ? `This cycle's bills were raised automatically on ${cycle.raisedOn} from the amounts on this property. Correct any figure that differs from the real bill — you can edit until the tenant approves it.`
                  : `A month's bill is raised once that month is over. ${cycle.cycleLabel}'s bills go out on ${cycle.raisedOn}.`}
                {cycle.isPartial &&
                  ` Tenancy started mid-cycle, so fixed charges are pro-rated to ${cycle.occupiedDays} of ${cycle.daysInMonth} days.`}
                {cycle.rentAlreadyCollected &&
                  " First month's rent was already collected with the advance, so there's no rent bill this cycle."}
                {cycle.rentCreditApplied > 0 &&
                  ` Rs ${cycle.rentCreditApplied.toLocaleString()} of advance-rent credit is deducted from this cycle's rent.`}
              </Typography>
            </Paper>
          )}

          <BillCards
            cards={visibleCards}
            isTenantUploader={isTenantUploader}
            onCardClick={handleOpenModal}
            statusFor={statusFor}
          />
        </Container>

        {/* ── Modal ─────────────────────────────────────────────────────────── */}
        <Modal
          open={!!modalOpen}
          onClose={handleCloseModal}
          closeAfterTransition
        >
          <Fade in={!!modalOpen}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "95%", sm: 700 },
                maxHeight: "92vh",
                overflowY: "auto",
                bgcolor: "#fff",
                borderRadius: 5,
                boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
                outline: "none",
              }}
            >
              {viewMode ? (
                <ViewOnlyModal cardData={cardData} onClose={handleCloseModal} />
              ) : (
                <form onSubmit={formik.handleSubmit}>
                  {/* Modal header */}
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${cardData?.color} 0%, ${cardData?.color}dd 100%)`,
                      p: 4,
                      position: "relative",
                      color: "#fff",
                      borderRadius: "20px 20px 0 0",
                    }}
                  >
                    <IconButton
                      onClick={handleCloseModal}
                      sx={{
                        position: "absolute",
                        right: 16,
                        top: 16,
                        color: "#fff",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          fontSize: "2rem",
                          backgroundColor: "rgba(255,255,255,0.2)",
                          border: "3px solid rgba(255,255,255,0.3)",
                        }}
                      >
                        {cardData?.icon}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        >
                          {isFixedChargeType ? "Generate" : "Upload"}{" "}
                          {cardData?.title} Bill
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {showAutoFill
                            ? "Auto-fill with consumer number, or fill details manually"
                            : isFixedChargeType
                            ? "Amount is set from your property details — just confirm the due date"
                            : "Fill in bill details and attach image or PDF"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* ✅ Tenant Info Banner — inside modal (compact) */}
                  <Box sx={{ px: 4, pt: 3, pb: 0 }}>
                    <TenantInfoBanner tenant={displayTenant} compact />
                  </Box>

                  {/* Consumer number section */}
                  {showAutoFill && (
                    <ConsumerNumberField
                      cardData={cardData}
                      consumerNumber={consumerNumber}
                      consumerNumberError={consumerNumberError}
                      autoFillLoading={autoFillLoading}
                      onChange={handleConsumerNumberChange}
                      onAutoFill={handleAutoFill}
                      onReset={handleConsumerReset}
                      consumerNumberLength={consumerNumLength}
                      billFetched={billFetched}
                      fetchedAmount={fetchedBillAmount}
                      fetchedDueDate={fetchedDueDate}
                    />
                  )}

                  {/* ── Form body ─────────────────────────────────────── */}
                  <Box sx={{ p: 4 }}>
                    <Grid container spacing={3}>
                      {/* Amount */}
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            backgroundColor: amountLocked
                              ? "#f1f5f9"
                              : "#f8fafc",
                            border: `2px solid ${
                              amountLocked ? "#cbd5e1" : "#e2e8f0"
                            }`,
                            opacity: amountLocked ? 0.72 : 1,
                            transition: "all 0.2s",
                            ...(!amountLocked && {
                              "&:focus-within": {
                                borderColor: cardData?.color,
                                backgroundColor: "#fff",
                                boxShadow: `0 0 0 3px ${cardData?.color}20`,
                              },
                            }),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <AttachMoneyIcon
                              sx={{
                                fontSize: 20,
                                color: amountLocked
                                  ? "#94a3b8"
                                  : cardData?.color,
                              }}
                            />
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: amountLocked ? "#64748b" : "#0f172a",
                                fontSize: "0.875rem",
                              }}
                            >
                              Total Amount *
                            </Typography>
                            {amountLocked && (
                              <Box
                                sx={{
                                  ml: "auto",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <LockIcon
                                  sx={{ fontSize: 13, color: "#94a3b8" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#94a3b8",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {isFixedChargeType
                                    ? "Set at registration"
                                    : "Auto-set"}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="e.g. 1230"
                            name="total_amount"
                            value={formik.values.total_amount}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[^0-9.]/g, "");
                              formik.setFieldValue("total_amount", v, true);
                            }}
                            onBlur={formik.handleBlur}
                            InputProps={{
                              readOnly: amountLocked,
                              startAdornment: (
                                <Box
                                  component="span"
                                  sx={{
                                    color: "#64748b",
                                    fontWeight: 600,
                                    mr: 0.5,
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  Rs.
                                </Box>
                              ),
                            }}
                            error={
                              formik.touched.total_amount &&
                              Boolean(formik.errors.total_amount)
                            }
                            helperText={
                              formik.touched.total_amount &&
                              formik.errors.total_amount
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: amountLocked
                                  ? "#e9eef4"
                                  : "#fff",
                                borderRadius: 2,
                                cursor: amountLocked ? "not-allowed" : "text",
                                "& fieldset": { borderColor: "transparent" },
                                "&:hover fieldset": {
                                  borderColor: "transparent",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "transparent",
                                },
                              },
                            }}
                          />
                        </Box>
                      </Grid>

                      {/* Due Date */}
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            backgroundColor: fieldsLocked
                              ? "#f1f5f9"
                              : "#f8fafc",
                            border: `2px solid ${
                              fieldsLocked ? "#cbd5e1" : "#e2e8f0"
                            }`,
                            opacity: fieldsLocked ? 0.72 : 1,
                            transition: "all 0.2s",
                            ...(!fieldsLocked && {
                              "&:focus-within": {
                                borderColor: cardData?.color,
                                backgroundColor: "#fff",
                                boxShadow: `0 0 0 3px ${cardData?.color}20`,
                              },
                            }),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <CalendarTodayIcon
                              sx={{
                                fontSize: 20,
                                color: fieldsLocked
                                  ? "#94a3b8"
                                  : cardData?.color,
                              }}
                            />
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: fieldsLocked ? "#64748b" : "#0f172a",
                                fontSize: "0.875rem",
                              }}
                            >
                              Due Date *
                            </Typography>
                            {fieldsLocked && (
                              <Box
                                sx={{
                                  ml: "auto",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <LockIcon
                                  sx={{ fontSize: 13, color: "#94a3b8" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#94a3b8",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Auto-set
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <MuiLocalizationProvider dateAdapter={AdapterDayjs}>
                            <MuiDatePicker
                              minDate={dayjs().add(1, "day")}
                              value={formik.values.due_date}
                              onChange={(v) => {
                                if (!fieldsLocked)
                                  formik.setFieldValue("due_date", v);
                              }}
                              readOnly={fieldsLocked}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  error:
                                    formik.touched.due_date &&
                                    Boolean(formik.errors.due_date),
                                  helperText:
                                    formik.touched.due_date &&
                                    formik.errors.due_date,
                                  sx: {
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: "#fff",
                                      borderRadius: 2,
                                      "& fieldset": {
                                        borderColor: "transparent",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: "transparent",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </MuiLocalizationProvider>
                        </Box>
                      </Grid>

                      {/* Message — hidden for fixed charges, they're just "confirm the due date" */}
                      {!isFixedChargeType && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            backgroundColor: "#f8fafc",
                            border: "2px solid #e2e8f0",
                            "&:focus-within": {
                              borderColor: cardData?.color,
                              backgroundColor: "#fff",
                              boxShadow: `0 0 0 3px ${cardData?.color}20`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <MessageIcon
                              sx={{ fontSize: 20, color: cardData?.color }}
                            />
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "#0f172a",
                                fontSize: "0.875rem",
                              }}
                            >
                              Message{" "}
                              <Typography
                                component="span"
                                sx={{
                                  fontWeight: 400,
                                  color: "#94a3b8",
                                  fontSize: "0.8rem",
                                }}
                              >
                                (Optional)
                              </Typography>
                            </Typography>
                          </Box>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            placeholder="Add a note for the tenant…"
                            name="message"
                            value={formik.values.message}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.message &&
                              Boolean(formik.errors.message)
                            }
                            helperText={
                              formik.touched.message && formik.errors.message
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#fff",
                                borderRadius: 2,
                                "& fieldset": { borderColor: "transparent" },
                                "&:hover fieldset": {
                                  borderColor: "transparent",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "transparent",
                                },
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                      )}

                      {/* File upload — hidden for fixed charges (no image needed) */}
                      {!isFixedChargeType && (
                      <Grid item xs={12}>
                        <FileUploadField
                          cardData={cardData}
                          imagePreview={imagePreview}
                          previewFile={previewFile}
                          required={requiresImage}
                          locked={fieldsLocked}
                          formikField={{
                            touched: formik.touched.bill_image,
                            error: formik.errors.bill_image,
                            setFieldValue: formik.setFieldValue,
                            setFieldTouched: formik.setFieldTouched,
                          }}
                          onFileSelect={
                            fieldsLocked ? undefined : handleFileSelect
                          }
                          onFileRemove={
                            fieldsLocked ? undefined : handleFileRemove
                          }
                          onDownloadPdf={handleDownloadPdf}
                          onOpenPdfNewTab={handleOpenPdfNewTab}
                        />
                      </Grid>
                      )}
                    </Grid>

                    <Divider sx={{ my: 4 }} />

                    <Stack direction="row" spacing={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleCloseModal}
                        disabled={isAnyLoading}
                        sx={{
                          borderRadius: 3,
                          py: 1.8,
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: "#64748b",
                          borderColor: "#e2e8f0",
                          borderWidth: 2,
                          "&:hover": {
                            borderWidth: 2,
                            borderColor: "#cbd5e1",
                            backgroundColor: "#f8fafc",
                          },
                          "&:disabled": {
                            borderColor: "#e2e8f0",
                            color: "#cbd5e1",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        startIcon={
                          isAnyLoading ? (
                            <CircularProgress
                              size={18}
                              sx={{ color: "#fff" }}
                            />
                          ) : (
                            <CloudUploadIcon />
                          )
                        }
                        disabled={isAnyLoading || autoFillLoading}
                        sx={{
                          borderRadius: 3,
                          py: 1.8,
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "1rem",
                          background: `linear-gradient(135deg, ${cardData?.color} 0%, ${cardData?.color}dd 100%)`,
                          boxShadow: `0 8px 24px ${cardData?.color}50`,
                          "&:hover": {
                            boxShadow: `0 12px 32px ${cardData?.color}60`,
                            transform: "translateY(-2px)",
                          },
                          "&:disabled": {
                            background: "#cbd5e1",
                            color: "#fff",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {isAnyLoading
                          ? "Generating…"
                          : isFixedChargeType
                          ? "Generate Bill"
                          : "Upload Bill"}
                      </Button>
                    </Stack>
                  </Box>
                </form>
              )}
            </Box>
          </Fade>
        </Modal>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="light"
        style={{ zIndex: 99999 }}
      />
    </>
  );
}
